// Barcode types

export const BARCODE_EAN8 = 'ean8';
export const BARCODE_EAN13 = 'ean13';
export const BARCODE_CODE39 = 'code39';
export const BARCODE_CODE128 = 'code128';
export const BARCODE_UPCA = 'upca';
export const BARCODE_GS_128 = 'gs1-128';
export const BARCODE_QRCODE = 'qrcode';
export const BARCODE_DATABAR_STACKED = 'databarexpandedstacked';

// Length of fixed length barcode types.
// Also enables check digit computation for these types.
export const BARCODELENGTH = {
  [BARCODE_EAN8]: 8,
  [BARCODE_UPCA]: 12,
  [BARCODE_EAN13]: 13,
};

/**
 * Compute GTIN check digit.
 * Supports GTIN-8, GTIN-12, GTIN-13 (= EAN-13), GTIN-14, GSIN, SSCC.
 * The last digit of the code is the check digit.
 *
 * @param {(string|number)} code Code without check digit
 * @returns {string} The check digit, or null if invalid code
 */
export function computeGTINCheckDigit(code: string | number): string | null {
  if (!/^\d{7,18}$/.test(String(code))) return null; // code must be digits only, 7-18 digits

  const digits: string[] = [...String(code).padStart(18, '0')];
  // weights: [1,3,1,3,...] length 18
  const weights: number[] = Array(9).fill([1, 3]).flat();
  const sum = weights.reduce(
    (accumulator: number, weight: number, i: number) =>
      accumulator + weight * parseInt(digits[i], 10),
    0
  );

  const remainder = sum % 10;
  return String(remainder ? 10 - remainder : 0);
}

export const BARCODEPREFIXES = {
  prefixOrderLoad: '$O',
  prefixOrderLoadByItem: '$I',
  prefixColourCode: '$K',
} as const;
export type BarcodePrefixKey = keyof typeof BARCODEPREFIXES;

/**
 * Encode string to CODE39 character set using $ as Unicode escape character.
 * ASCII letters are uppercased, other non-39 characters and the $ are represented
 * by '$' + 4 hex digits.
 */
export function code39Encode(str: string | null): string | null {
  if (!str) return null;
  const r = str.replace(
    /[^-./+%a-zA-Z\d]/g,
    (char) => '$' + char.charCodeAt(0).toString(16).padStart(4, '0')
  );
  return r.toUpperCase();
}

const EAN13_LENGTH = 13;

/**
 * Encode string (with optional prefix) to EAN13 barcode, adding the check digit.
 */
export function encodeEAN13(
  code: string | null,
  prefix: string = ''
): string | null {
  if (!code) return null;
  const padded = prefix + code.padStart(EAN13_LENGTH - 1 - prefix.length, '0');
  if (padded.length !== EAN13_LENGTH - 1) {
    return null;
  }
  const check = computeGTINCheckDigit(padded);
  return check != null ? padded + check : '';
}

const DATABAR_PRODUCT_PREFIX = '(01)';
const DATABAR_PRODUCT_PREFIX_WITHOUT_BRACKETS = '01';
const DATABAR_PRODUCT_LENGTH = 14;
const DATABAR_PRICE_PREFIX = '(92)001';
const DATABAR_PRICE_LENGTH = 14;
const DATABAR_COLOR_CODE_PREFIX = '(240)';

const DATABAR_NUMERIC_MAX_LENGTH = 74;
const DATABAR_ALPHANUMERIC_MAX_LENGTH = 41;

const REGEXP_GS1_NUMERIC_BARCODE = /^[\d()]*$/;

export function filterGS1Chars(code: string): string {
  // The GS1 subset of international standard ISO/IEC 646

  return code.replace(/[^!"%&'()*+,./0-9:;<=>?A-Z_a-z]/g, '');
}

export function encodeGS1ProductBarcode(
  productBarcode?: string | number | null
): string {
  if (!productBarcode) return '';

  let code = String(productBarcode || '');
  code = code.padStart(EAN13_LENGTH, '0');
  const checkDigit = computeGTINCheckDigit(code.substring(0, 12));
  const isEAN = code.charAt(12) === checkDigit;
  const prefix = isEAN
    ? DATABAR_PRODUCT_PREFIX
    : DATABAR_PRODUCT_PREFIX_WITHOUT_BRACKETS;
  return prefix + code.padStart(DATABAR_PRODUCT_LENGTH, '0');
}

export function encodeGS1Price(
  priceBarcodeValue: string | number | null | undefined
): string {
  const v = priceBarcodeValue == null ? '' : String(priceBarcodeValue);
  return v ? DATABAR_PRICE_PREFIX + v.padStart(DATABAR_PRICE_LENGTH, '0') : '';
}

export function encodeGS1ColorCode(
  colorCode: string | null | undefined
): string {
  return colorCode ? DATABAR_COLOR_CODE_PREFIX + filterGS1Chars(colorCode) : '';
}

/**
 * Cut GS1 barcode to maximum length. The maximum depends on content.
 */
export function limitGS1Length(barcode: string | null | undefined): string {
  if (!barcode) return '';

  //Calculate max length
  let maxLength = REGEXP_GS1_NUMERIC_BARCODE.test(barcode)
    ? DATABAR_NUMERIC_MAX_LENGTH
    : DATABAR_ALPHANUMERIC_MAX_LENGTH;

  // Allow brackets
  const brackets = barcode.match(/[()]/g);
  maxLength += brackets ? brackets.length : 0;

  // Allow (01)
  if (barcode.startsWith(DATABAR_PRODUCT_PREFIX))
    // Brackets already handled, so using just length of "01" string
    maxLength += DATABAR_PRODUCT_PREFIX_WITHOUT_BRACKETS.length;

  return barcode.substring(0, maxLength);
}
